/**
 * Data layer for `post-affiliate-pro-integration-via-api`. Thin typed fetch wrappers over /api/v1/post-affiliate-pro-integration-via-api.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PostAffiliateProIntegrationViaApiRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPostAffiliateProIntegrationViaApiResponse {
	count: number;
	items: PostAffiliateProIntegrationViaApiRow[];
}

export interface CreatePostAffiliateProIntegrationViaApiBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/post-affiliate-pro-integration-via-api";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPostAffiliateProIntegrationViaApiResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPostAffiliateProIntegrationViaApiResponse>(res);
}

export async function create(body: CreatePostAffiliateProIntegrationViaApiBody): Promise<PostAffiliateProIntegrationViaApiRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PostAffiliateProIntegrationViaApiRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const postAffiliateProIntegrationViaApiQueryKeys = {
	all: ["post-affiliate-pro-integration-via-api"] as const,
	list: () => [...postAffiliateProIntegrationViaApiQueryKeys.all] as const,
	detail: (id: string) => [...postAffiliateProIntegrationViaApiQueryKeys.all, id] as const,
};
